import axios from "axios";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import statesData from "../states.json";

import FilterToolbar from "./FilterToolbar";
import Footer from "../siteComponents/Footer";
import TopBar from "../siteComponents/Topbar";

const COMPANY_CATEGORIES = [
    "Aerospace and Space Technology",
    "Agriculture and AgTech",
    "Beauty & Personal Care",
    "Biotechnology",
    "Carbon Capture & Climate Technology",
    "Conservation & Biodiversity",
    "Consumer Applications",
    "Consumer Packaged Goods (CPG)",
    "Education and EdTech",
    "Financial Services and Fintech",
    "Food Service and Hospitality",
    "Gaming and Esports",
    "Health Care Equipment & Supplies",
    "Health Care Providers & Services",
    "Health Care Technology",
    "Insurance and InsurTech",
    "Life Sciences Tools & Services",
    "Manufacturing and Industrial/Hardware",
    "Materials, Chemicals and Sustainable Packaging",
    "Media and Entertainment",
    "Pharmaceuticals",
    "Professional and Business Services",
    "Real Estate",
    "Renewable & Clean Energy",
    "Retail",
    "Sports, Fitness & Wellness",
    "Technology",
    "Transportation and Mobility",
    "Waste Management & Circular Economy",
    "Water Technology & Conservation",
    "Other",
];

const INVESTMENT_RANGE_OPTIONS = [
    { label: "All Minimum Investments", value: "" },
    { label: "Under $500", value: "under-500" },
    { label: "$500 - $999", value: "500-999" },
    { label: "$1,000 - $2,499", value: "1000-2499" },
    { label: "$2,500 - $4,999", value: "2500-4999" },
    { label: "$5,000 and above", value: "5000-plus" },
];

const TIMELINE_RANGE_OPTIONS = [
    { label: "All Time Left", value: "" },
    { label: "0 to 2 Months", value: "0-2" },
    { label: "2 to 4 Months", value: "2-4" },
    { label: "4 to 6 Months", value: "4-6" },
    { label: "6 to 8 Months", value: "6-8" },
    { label: "8 to 10 Months", value: "8-10" },
    { label: "10 to 12 Months", value: "10-12" },
];

const STATE_OPTIONS = Object.entries(statesData).map(([value, label]) => ({
    value,
    label,
}));

function normalizeCategory(value) {
    return (value || "").trim().toLowerCase();
}

function parseAmount(value) {
    const normalized = Number.parseFloat(String(value || "").replace(/,/g, ""));
    return Number.isFinite(normalized) ? normalized : 0;
}

function matchesInvestmentRange(amount, selectedRange) {
    if(!selectedRange) return true;

    switch(selectedRange) {
        case "under-500":
            return amount < 500;
        case "500-999":
            return amount >= 500 && amount <= 999;
        case "1000-2499":
            return amount >= 1000 && amount <= 2499;
        case "2500-4999":
            return amount >= 2500 && amount <= 4999;
        case "5000-plus":
            return amount >= 5000;
        default:
            return true;
    }
}

function getMonthsRemaining(expiry) {
    if(!expiry) return null;

    const now = new Date();
    const expiryDate = new Date(expiry);
    const diffMs = expiryDate - now;

    if(diffMs <= 0) return null;

    return diffMs / 86_400_000 / 30.4375;
}

function matchesTimelineRange(expiry, selectedRange) {
    if(!selectedRange) return true;

    const monthsRemaining = getMonthsRemaining(expiry);

    if(monthsRemaining === null) return false;

    switch(selectedRange) {
        case "0-2":
            return monthsRemaining >= 0 && monthsRemaining <= 2;
        case "2-4":
            return monthsRemaining > 2 && monthsRemaining <= 4;
        case "4-6":
            return monthsRemaining > 4 && monthsRemaining <= 6;
        case "6-8":
            return monthsRemaining > 6 && monthsRemaining <= 8;
        case "8-10":
            return monthsRemaining > 8 && monthsRemaining <= 10;
        case "10-12":
            return monthsRemaining > 10 && monthsRemaining <= 12;
        default:
            return true;
    }
}

function Countdown({ expiry }) {
    Countdown.propTypes = {
        expiry: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.instanceOf(Date),
        ]).isRequired,
    };
    const [display, setDisplay] = React.useState(getTimeLeft(expiry));

    React.useEffect(() => {
        const id = setInterval(() => setDisplay(getTimeLeft(expiry)), 1000);
        return () => clearInterval(id);
    }, [expiry]);

    return <>{display === "ENDED" ? "ENDED" : `${display} LEFT`}</>;
}

function getTimeLeft(expiryISO) {
    const now = new Date();
    const expiry = new Date(expiryISO);
    const diffMs = expiry - now;
    if(diffMs <= 0) return "ENDED";

    const MS_SEC = 1_000;
    const MS_HOUR = 3_600_000;
    const MS_DAY = 86_400_000;

    /* ── < 1 h  →  MM:SS ───────────────────────────── */
    if(diffMs < MS_HOUR) {
        const totalSec = Math.floor(diffMs / MS_SEC);
        const m = Math.floor(totalSec / 60);
        const s = totalSec % 60;
        return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }

    /* ── < 1 day →  H HR MM MIN SS SEC ─────────────── */
    if(diffMs < MS_DAY) {
        const totalSec = Math.floor(diffMs / MS_SEC);
        const h = Math.floor(totalSec / 3600);
        const m = Math.floor((totalSec % 3600) / 60);
        const s = totalSec % 60;
        return `${h} HR${h !== 1 ? "S" : ""} ${String(m).padStart(
            2,
            "0",
        )} MIN ${String(s).padStart(2, "0")} SEC`;
    }

    /* ── ≥ 1 day  →  months & days calculation ─────── */
    // months difference using calendar math
    let months =
        (expiry.getFullYear() - now.getFullYear()) * 12 +
        (expiry.getMonth() - now.getMonth());

    if(now.getDate() > expiry.getDate()) months--;

    // days remaining after whole months
    const nextMonth = new Date(now);
    nextMonth.setMonth(now.getMonth() + months);
    const days = Math.floor((expiry - nextMonth) / MS_DAY);

    if(months >= 1) {
        return days > 0
            ? `${months} MONTH${months !== 1 ? "S" : ""} ${days} DAY${
                  days !== 1 ? "S" : ""
              }`
            : `${months} MONTH${months !== 1 ? "S" : ""}`;
    }
    /* 1–29 days */
    return `${days} DAY${days !== 1 ? "S" : ""}`;
}

function PublicCampaignsPage({ initialViewMode = "grid" }) {
    const [campaigns, setCampaigns] = useState([]);
    const [viewMode, setViewMode] = useState(initialViewMode);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedIndustry, setSelectedIndustry] = useState("");
    const [selectedLocation, setSelectedLocation] = useState("");
    const [selectedInvestmentRange, setSelectedInvestmentRange] = useState("");
    const [selectedTimelineRange, setSelectedTimelineRange] = useState("");
    const navigate = useNavigate();

    const clearAllFilters = () => {
        setSelectedIndustry("");
        setSelectedLocation("");
        setSelectedInvestmentRange("");
        setSelectedTimelineRange("");
    };

    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_BACKEND_URL}/api/public-campaigns`)
            .then((res) => {
                // Only show campaigns that are not deleted and not withdrawn
                const filtered = (res.data || []).filter(
                    (c) => c.is_deleted !== 1 && c.status !== "withdrawn",
                );
                // For each campaign, set expiry to end_date if present, else 12 months from approved_at
                const campaignsWithExpiry = filtered.map((c) => {
                    let expiry = null;
                    if(["completed", "closed"].includes(c.status)) {
                        expiry = null;
                    } else if(c.end_date) {
                        expiry = new Date(c.end_date);
                        // If end_date is in the past, mark as ended
                        if(expiry < new Date()) expiry = null;
                        else expiry = expiry.toISOString();
                    } else if(c.approved_at) {
                        expiry = new Date(c.approved_at);
                        expiry.setFullYear(expiry.getFullYear() + 1);
                        if(expiry < new Date()) expiry = null;
                        else expiry = expiry.toISOString();
                    }
                    return { ...c, expiry };
                });
                setCampaigns(campaignsWithExpiry);
            })
            .catch((err) => console.error("Error fetching campaigns", err));
    }, []);

    const filteredCampaigns = campaigns.filter((campaign) => {
                const normalizedSearch = searchTerm.trim().toLowerCase();
                const matchesSearch = normalizedSearch
                        ? [campaign.campaign_name, campaign.company_name].some((value) =>
                                    String(value || "").toLowerCase().includes(normalizedSearch),
                            )
                        : true;

        const matchesIndustry = selectedIndustry
            ? normalizeCategory(campaign.industry) ===
              normalizeCategory(selectedIndustry)
            : true;

        const matchesLocation = selectedLocation
            ? campaign.companyState === selectedLocation
            : true;

        const matchesInvestment = matchesInvestmentRange(
            parseAmount(campaign.investmentMinimum),
            selectedInvestmentRange,
        );

        const matchesTimeline = matchesTimelineRange(
            campaign.expiry,
            selectedTimelineRange,
        );

        return (
            matchesSearch &&
            matchesIndustry &&
            matchesLocation &&
            matchesInvestment &&
            matchesTimeline
        );
    });

    const hasSelectedFilters =
        selectedIndustry ||
        selectedLocation ||
        selectedInvestmentRange ||
        selectedTimelineRange;

    const noCampaignsMessage = searchTerm
        ? "No campaigns found for your search. Please try a different campaign or company name."
        : hasSelectedFilters
            ? "No campaigns found for the selected filters. Please try different filter values."
            : "No campaigns at this time. Please check back soon!";

    // ── Column definitions (order matches the mock-up)
    const campaignColumns = [
        {
            name: "CAMPAIGN NAME",
            selector: (row) => row.campaign_name,
            cell: (row) => (
                <span
                    className="font-medium cursor-pointer hover:text-[#782375] transition-colors"
                    onClick={() => navigate(`/campaigns/view/${row.id}`)}
                >
                    {row.campaign_name}
                </span>
            ),
            wrap: true,
            sortable: true,
            ignoreRowClick: true,
        },
        {
            name: "COMPANY NAME",
            selector: (row) => row.company_name,
            cell: (row) => (
                <span
                    className="cursor-pointer hover:text-[#782375] transition-colors"
                    onClick={() => navigate(`/campaigns/view/${row.id}`)}
                >
                    {row.company_name}
                </span>
            ),
            wrap: true,
            sortable: true,
            ignoreRowClick: true,
        },
        {
            name: "AMOUNT INVESTED",
            selector: (row) => row.amount,
            cell: (row) => (
                <span
                    className="cursor-pointer hover:text-[#782375] transition-colors"
                    onClick={() => navigate(`/campaigns/view/${row.id}`)}
                >
                    ${Number(row.amount || 0).toLocaleString()}
                </span>
            ),
            sortable: true,
            right: true,
            ignoreRowClick: true,
        },
        {
            name: "CAMPAIGN STATUS",
            cell: (row) => (
                <div
                    className="text-center cursor-pointer hover:text-[#782375] transition-colors"
                    onClick={() => navigate(`/campaigns/view/${row.id}`)}
                >
                    {row.status?.toUpperCase() ?? "OPEN"}
                </div>
            ),
            sortable: true,
            ignoreRowClick: true,
        },
        {
            name: "INDUSTRY",
            selector: (row) => row.industry,
            cell: (row) => (
                <span
                    className="cursor-pointer hover:text-[#782375] transition-colors"
                    onClick={() => navigate(`/campaigns/view/${row.id}`)}
                >
                    {row.industry}
                </span>
            ),
            sortable: true,
            wrap: true,
            ignoreRowClick: true,
        },
        {
            name: "UPDATES",
            cell: (row) =>
                row.updates_count ? (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/campaigns/view/${row.id}#updates`);
                        }}
                        className="text-[#782375] underline font-medium hover:text-[#5a1b56] transition-colors"
                    >
                        {row.updates_count} update{row.updates_count > 1 && "s"}
                    </button>
                ) : (
                    <span
                        className="cursor-pointer"
                        onClick={() => navigate(`/campaigns/view/${row.id}`)}
                    >
                        —
                    </span>
                ),
            ignoreRowClick: true,
            center: true,
        },
        {
            name: "TIMELINE",
             cell: (row) => (
                <div
                    className="text-center cursor-pointer hover:text-[#782375] transition-colors"
                    onClick={() => navigate(`/campaigns/view/${row.id}`)}
                >
                    {row.expiry && (
                        <div className="text-[10px] text-[#8B1C2D] font-semibold">
                            <Countdown expiry={row.expiry} />
                        </div>
                    )}
                </div>
            ),
            sortable: true,
            ignoreRowClick: true,
        },
    ];

    const borderColor = "#D1E3EC";

    const tableStyles = {
        table: {
            style: { border: `1px solid ${borderColor}` },
        },
        headRow: {
            style: {
                backgroundColor: "#E8F0F4",
                minHeight: "70px",
                borderBottom: `1px solid ${borderColor}`,
            },
        },
        headCells: {
            style: {
                /* centre horizontally inside RDT’s flex cell */
                justifyContent: "center",
                alignItems: "center",

                /* typography */
                color: "var(--navy)",
                fontWeight: 600,
                fontSize: "14px",
                textTransform: "uppercase",

                /* padding + vertical grid line */
                paddingLeft: "24px",
                paddingRight: "24px",
                borderRight: `1px solid ${borderColor}`,
            },
        },
        rows: {
            style: {
                minHeight: "72px",
                borderBottom: `1px solid ${borderColor}`,
            },
        },
        cells: {
            style: {
                /* centre content */
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",

                /* roomy sides */
                paddingLeft: "24px",
                paddingRight: "24px",

                /* vertical grid line */
                borderRight: `1px solid ${borderColor}`,
            },
        },
    };

    // console.log('viewMode going to FilterToolbar ➜', viewMode);

    return (
        <>
            <TopBar />
            {/* Section 1 - Investor Banner */}
            <section className="relative flex h-[200px] w-full items-center justify-center overflow-hidden bg-navy px-4 sm:px-6 lg:px-8">
                <div
                    className="absolute inset-0 opacity-[0.07]"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
                        backgroundSize: "24px 24px",
                    }}
                />
                <h1 className="relative text-center text-3xl font-semibold text-white md:text-[2.3rem]">
                    INVESTMENT OPTIONS
                </h1>
            </section>

            <FilterToolbar
                viewMode={viewMode}
                onToggleView={setViewMode}
                onClearAllFilters={clearAllFilters}
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                selectedIndustry={selectedIndustry}
                onSelectIndustry={setSelectedIndustry}
                industryOptions={COMPANY_CATEGORIES}
                selectedLocation={selectedLocation}
                onSelectLocation={setSelectedLocation}
                locationOptions={STATE_OPTIONS}
                selectedInvestmentRange={selectedInvestmentRange}
                onSelectInvestmentRange={setSelectedInvestmentRange}
                investmentRangeOptions={INVESTMENT_RANGE_OPTIONS}
                selectedTimelineRange={selectedTimelineRange}
                onSelectTimelineRange={setSelectedTimelineRange}
                timelineRangeOptions={TIMELINE_RANGE_OPTIONS}
            />

            {/* ───── Campaign list ───── */}

            {filteredCampaigns.length === 0 ? (
                <div className="px-3 sm:px-4 lg:px-6 xl:px-8 py-12 sm:py-16 max-w-7xl mx-auto bg-[#F5F7FA] text-center">
                    <p className="text-lg sm:text-xl text-navy font-poppins font-medium">
                        {noCampaignsMessage}
                    </p>
                </div>
            ) : viewMode === "grid" ? (
                <div className="px-3 sm:px-4 lg:px-6 xl:px-8 py-6 sm:py-8 max-w-7xl 2xl:max-w-screen-2xl mx-auto bg-[#F5F7FA]">
                    <div
                        className="grid gap-4 sm:gap-5 lg:gap-6 
                                   grid-cols-1 
                                   sm:grid-cols-2 
                                   lg:grid-cols-3 
                                   xl:grid-cols-4 
                                   2xl:grid-cols-5"
                    >
                        {filteredCampaigns.map((campaign) => (
                            <div
                                key={campaign.id}
                                onClick={() =>
                                    navigate(`/campaigns/view/${campaign.id}`)
                                }
                                className="cursor-pointer bg-white border border-gray-300 rounded-xl overflow-hidden shadow hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                            >
                                <div className="relative">
                                    <img
                                        src={campaign.campaign_background_image}
                                        alt={campaign.campaign_name}
                                        className="h-40 sm:h-44 lg:h-48 w-full object-cover"
                                    />
                                    <span className="absolute top-2 left-2 bg-[#8B1C2D] text-white text-xs px-2 py-1 rounded">
                                        {campaign.status === "completed" ? (
                                            "COMPLETED"
                                        ) : campaign.status === "closed" ? (
                                            "CLOSED"
                                        ) : campaign.expiry ? (
                                            <Countdown
                                                expiry={campaign.expiry}
                                            />
                                        ) : (
                                            "ENDED"
                                        )}
                                    </span>
                                    {campaign.first_founder_headshot && (
                                        <img
                                            src={
                                                campaign.first_founder_headshot
                                            }
                                            alt="Founder"
                                            className="absolute bottom-2 right-2 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full border-2 border-white shadow-lg object-cover"
                                        />
                                    )}
                                </div>
                                <div className="p-3 sm:p-4">
                                    <p className="text-xs sm:text-sm text-navy font-semibold uppercase truncate">
                                        {campaign.company_name}
                                    </p>
                                    <h2 className="text-[#8B1C2D] font-bold text-base sm:text-lg mb-1 line-clamp-2">
                                        {campaign.campaign_name}
                                    </h2>
                                    <p className="text-xs sm:text-sm text-gray-600 line-clamp-3">
                                        {campaign.campaign_description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="w-full flex justify-center px-0 py-4 bg-[#F5F7FA]">
                    <div
                        className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto p-0"
                        style={{ padding: 0 }}
                    >
                        <div
                            className="w-full max-w-[1400px] mx-auto"
                            style={{
                                overflowX: "auto",
                                overflowY: "hidden",
                                WebkitOverflowScrolling: "touch",
                                scrollbarWidth: "auto",
                                scrollbarColor: "var(--navy) #E5E7EB",
                                marginLeft: "auto",
                                marginRight: "auto",
                                border: "none",
                                boxShadow: "none",
                                background: "transparent",
                                padding: 0,
                                maxWidth: "1400px",
                                width: "100%",
                            }}
                        >
                            <DataTable
                                columns={campaignColumns}
                                data={filteredCampaigns}
                                customStyles={{
                                    ...tableStyles,
                                    table: {
                                        style: {
                                            border: "none",
                                            width: "100%",
                                            minWidth: "1200px",
                                            maxWidth: "1400px",
                                            tableLayout: "fixed",
                                            marginLeft: "auto",
                                            marginRight: "auto",
                                            background: "transparent",
                                        },
                                    },
                                }}
                                noDataComponent={noCampaignsMessage}
                                pointerOnHover={false}
                                highlightOnHover={false}
                                dense={false}
                                responsive={false}
                            />
                        </div>
                    </div>
                </div>
            )}
            <Footer />
        </>
    );
}

export default PublicCampaignsPage;

PublicCampaignsPage.propTypes = {
    initialViewMode: PropTypes.oneOf(["grid", "table"]),
};
